import { count } from '../core/words.js';
import { ft } from '../core/units.js';
import { EVENT } from './events.js';
import { formatSpan, formatTime, parseTime } from './clock.js';
import { gradeTeardown } from './grading.js';
import { stackLimit } from './mass.js';
import { OBJECTS } from '../data/objects.js';
import { TEARDOWNS, TEARDOWNS_BY_ID } from '../data/teardowns.js';
import { WHEELED } from '../data/wheeled.js';
import { LEVEL } from '../data/level.js';

/**
 * Striking an outdoor function: the card on the board, the crew on the lawn,
 * and what got to the dock.
 *
 * Three things, and none of them is a new system:
 *
 *   the card    a pure function of the day, like the rest of the board, put
 *               on it after the generated events and marked `ghosts: false`,
 *               because a lawn nobody strikes is not struck by somebody else
 *   the crew    the colleagues whose rows say `teardown`, posted to the lawn
 *               and walking it — stack, cart, stack — with their eyes open and
 *               their shapes intact. What they see is perception's business
 *               and what they make of it is suspicion's. The only things this
 *               file does to them are move them and, when you keep them
 *               standing there or run one over, tell their personal ledger,
 *               through the same `think` a branch in the face goes through
 *   the grade   the carts on the dock, what went over on the way, and the time
 *
 * The towing itself is the wheeled class. Nothing here moves a cart except to
 * put the lawn's carts back where they live when a new strike is taken.
 */

/*
 * Seconds at a stack folding or stacking an armful, and at a cart putting it
 * on. Real seconds, because it is hands doing a thing. Slow enough that the
 * crew are still loading the last cart while the first one is being towed,
 * which is how a strike goes when the driver is any good.
 */
const AT_STACK = 10;
const AT_CART = 4;

/*
 * Waiting on you, in game minutes, because it is a fact about their afternoon.
 *
 * Towing is one cart at a time, so the crew are always going to stand about
 * for some of it, and that is not you keeping them waiting — that is the job.
 * What is: loaded carts on the lawn, or somebody stood holding an armful with
 * nowhere to put it, while nobody is working the tow at all. Working it is
 * having the golf cart, or one of the lawn's carts, in your hands or on the
 * ball. Twenty minutes of that is a driver who went for a coffee; after that,
 * every quarter of an hour is another two off what each of them thinks of you.
 * Small, the size of a branch in the face, and it adds up.
 */
const WAIT_GRACE = 20;
const WAIT_EVERY = 15;
const REGARD_WAITED = -2;

/** Clipped with a loaded cart on a drawbar. A bigger thing than a branch. */
const REGARD_CLIPPED = -4;

const clockText = (minutes) => {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

/** "2 table carts, 2 chair carts", off the carts' own rows. */
function cartLine(ids) {
  const count = new Map();
  for (const id of ids) {
    const kind = WHEELED.find((entry) => entry.id === id)?.kind;
    count.set(kind, (count.get(kind) ?? 0) + 1);
  }
  return [...count]
    .map(([kind, n]) => `${n} ${n === 1 ? OBJECTS[kind]?.name.toLowerCase() : OBJECTS[kind]?.plural ?? 'carts'}`)
    .join(', ');
}

/** The card for one lawn on one day. Pure. */
export function cardFor(row, day, shift) {
  const start = parseTime(shift.start);
  const total = row.stacks.reduce((sum, stack) => sum + stack.count, 0);
  return {
    id: `teardown-${row.id}-d${day}`,
    code: `STK ${100 + day}`,
    title: row.title,
    client: row.client,
    room: row.lawn,
    teardown: row.id,
    // Not a room nobody set: a lawn nobody struck. See `beo.js`.
    ghosts: false,
    owner: 'banquets',
    postedAt: clockText(start + row.posts.at),
    setupBy: clockText(start + row.posts.due),
    // Half an hour's grace like a room's: late costs the grade, and only a
    // lawn still standing at the end of it is a write-up.
    doors: clockText(start + row.posts.due + 30),
    shortfalls: [],
    award: { ...row.award },
    estimate: row.estimate,
    note: row.note,
    lines: [
      ['Where', LEVEL.rooms.find((room) => room.id === row.lawn)?.name ?? row.lawn],
      [
        'Load',
        row.stacks.map((stack) => `${stack.count} ${OBJECTS[stack.kind].plural ?? OBJECTS[stack.kind].name}`).join(', '),
      ],
      ['Carts', cartLine(row.carts)],
      ['To', 'the loading dock'],
      ['Pieces', String(total)],
    ],
  };
}

export function createTeardown({
  wheeled,
  npcs,
  bus,
  clock,
  level,
  hud,
  think = () => {},
  propAt,
  shiftFor,
  /** The event you are on. A strike that stopped being it is over, however. */
  activeBeo = () => null,
  rows = TEARDOWNS,
  byId = TEARDOWNS_BY_ID,
}) {
  const strikers = () => npcs.filter((npc) => npc.data.teardown);
  let job = null;

  const cartById = (id) => wheeled.byId(id);
  const roomOf = (cart) => level.roomAt(cart.position.x, cart.position.z)?.id ?? null;
  const nameOf = (npc) => npc.data.name.split(' ')[0];

  /** Where a colleague stands to take an armful off a stack. Metres. */
  function stackSpot(stack) {
    const prop = propAt(stack.prop);
    return { x: prop.x, z: prop.z + prop.halfZ + ft(1.5) };
  }

  /** Beside a cart, clear of the end a golf cart backs up to. */
  const cartSpot = (cart) => ({ x: cart.position.x + ft(2.6), z: cart.position.z });
  const homeSpot = (cart) => ({ x: cart.home.x + ft(2.6), z: cart.home.z });

  const room = (cart, kind) => cart.hold.room(kind);

  /** A cart on the lawn, on its wheels, nobody's hands on it, with room for this. */
  function loadable(stack) {
    for (const id of job.row.carts) {
      const cart = cartById(id);
      if (!cart || cart.definition.id !== stack.cart) continue;
      if (cart.state !== 'parked' || cart.tipped || cart.towedBy) continue;
      if (roomOf(cart) !== job.row.lawn) continue;
      if (cart.hold.kind && cart.hold.kind !== stack.kind) continue;
      if (room(cart, stack.kind) > 0) return cart;
    }
    return null;
  }

  function loadedOnLawn() {
    return job.row.carts
      .map(cartById)
      .filter((cart) => cart && !cart.tipped && cart.hold.count > 0 && roomOf(cart) === job.row.lawn);
  }

  /** The stack with the most armfuls left on it. */
  function nextStack() {
    let best = null;
    for (const stack of job.row.stacks) {
      const left = job.left[stack.prop] ?? 0;
      if (!left) continue;
      const armfuls = left / stackLimit(stack.kind);
      if (!best || armfuls > best.armfuls) best = { stack, armfuls };
    }
    return best?.stack ?? null;
  }

  function stackFor(prop) {
    return job.row.stacks.find((stack) => stack.prop === prop) ?? null;
  }

  function counts() {
    if (!job) return null;
    const total = job.row.stacks.reduce((sum, stack) => sum + stack.count, 0);
    const kinds = new Set(job.row.stacks.map((stack) => stack.kind));
    let delivered = 0;
    let onDock = 0;
    let loaded = 0;
    for (const id of job.row.carts) {
      const cart = cartById(id);
      if (!cart) continue;
      if (cart.hold.count && kinds.has(cart.hold.kind)) loaded += cart.hold.count;
      if (cart.tipped || roomOf(cart) !== job.row.dock) continue;
      if (cart.hold.count && kinds.has(cart.hold.kind)) {
        delivered += cart.hold.count;
        onDock += 1;
      }
    }
    const totalMass = job.row.stacks.reduce((sum, stack) => sum + stack.count * (OBJECTS[stack.kind].mass ?? 0), 0);
    const spilled = job.spilled.reduce((sum, entry) => sum + entry.count, 0);
    const spilledMass = job.spilled.reduce((sum, entry) => sum + entry.count * (OBJECTS[entry.kind]?.mass ?? 0), 0);
    const left = Object.values(job.left).reduce((sum, n) => sum + n, 0);
    return { total, delivered, onDock, carts: job.row.carts.length, loaded, left, spilled, spilledMass, totalMass };
  }

  // --- what the lawn's carts go through ----------------------------------------

  wheeled.onChange((event) => {
    if (!job || !event.tipped || !event.spilled) return;
    if (!job.row.carts.includes(event.cart.id)) return;
    job.spilled.push({ cart: event.cart.id, kind: event.kind, count: event.spilled });
    hud?.say(`The ${event.cart.definition.name.toLowerCase()} went over. ${event.spilled} on the ground.`, { tone: 'bad', seconds: 4 });
  });

  /*
   * Somebody clipped by a cart on a drawbar.
   *
   * The impact is on the bus where anybody may act on it, and what is done
   * with it here is what `riders.js` does with a branch: the person it happened
   * to says so, and if they have an opinion of you it moves. Anybody who saw it
   * happen saw a cart hit a person, which is perception's.
   */
  bus.on(EVENT.IMPACT, (event) => {
    if (event.reason !== 'towed' || event.target?.type !== 'body') return;
    const npc = npcs.find((candidate) => candidate.data.id === event.target.id);
    if (!npc) return;
    npc.say('Oi! The cart!', 3);
    if (event.actor === 'player') think(npc.data.id, REGARD_CLIPPED, `You clipped ${nameOf(npc)} with the tow`);
  });

  // --- the crew ------------------------------------------------------------------

  function crewState(id) {
    if (!job.crew[id]) job.crew[id] = { phase: 'stack', stack: null, carrying: 0, busy: 0, waited: 0, charged: 0, posted: false };
    return job.crew[id];
  }

  function post(npc, state, spot, dwell) {
    npc.post([{ x: spot.x, z: spot.z, dwell }], { from: !state.posted });
    state.posted = true;
  }

  function work(npc, dt, minutes) {
    const state = crewState(npc.data.id);
    if (state.phase === 'released') return;
    if (!npc.present || npc.aboard) return;

    let waiting = false;
    const arrived = npc.postedAt?.arrived && !npc.searching && !npc.holding;

    if (state.phase === 'stack') {
      const stack = stackFor(state.stack) && job.left[state.stack] ? stackFor(state.stack) : nextStack();
      if (!stack) {
        state.phase = 'done';
      } else {
        state.stack = stack.prop;
        post(npc, state, stackSpot(stack), 0);
        if (arrived) {
          state.busy += dt;
          if (state.busy >= AT_STACK) {
            state.busy = 0;
            const take = Math.min(stackLimit(stack.kind), job.left[stack.prop]);
            job.left[stack.prop] -= take;
            state.carrying = take;
            state.phase = 'carry';
          }
        } else {
          state.busy = 0;
        }
      }
    }

    if (state.phase === 'carry') {
      const stack = stackFor(state.stack);
      const cart = loadable(stack);
      if (!cart) {
        waiting = true;
        const first = job.row.carts.map(cartById).find((entry) => entry?.definition.id === stack.cart);
        post(npc, state, first ? homeSpot(first) : stackSpot(stack), 0);
        state.busy = 0;
      } else {
        post(npc, state, cartSpot(cart), 0);
        if (arrived && Math.hypot(npc.position.x - cartSpot(cart).x, npc.position.z - cartSpot(cart).z) < 0.3) {
          state.busy += dt;
          if (state.busy >= AT_CART) {
            state.busy = 0;
            const put = Math.min(state.carrying, room(cart, stack.kind));
            for (let i = 0; i < put; i += 1) cart.hold.gain(stack.kind);
            state.carrying -= put;
            if (!state.carrying) state.phase = 'stack';
          }
        } else if (!arrived) {
          state.busy = 0;
        }
      }
    }

    if (state.phase === 'done') {
      const still = loadedOnLawn();
      if (!still.length) {
        state.phase = 'released';
        npc.unpost();
        return;
      }
      waiting = true;
      post(npc, state, homeSpot(still[0]), 0);
    }

    if (!waiting || minutes <= 0 || onIt()) return;
    state.waited += minutes;
    const due = Math.floor(Math.max(0, state.waited - WAIT_GRACE) / WAIT_EVERY) + (state.waited > WAIT_GRACE ? 1 : 0);
    while (state.charged < due) {
      state.charged += 1;
      npc.say(state.charged === 1 ? 'Still waiting on that cart.' : 'We are all just standing here.', 4);
      think(npc.data.id, REGARD_WAITED, `You kept ${nameOf(npc)} waiting on the lawn`);
    }
  }

  /** Somebody is working the tow: a tug or one of the lawn's carts in hand or on a ball. */
  function onIt() {
    const held = wheeled.active;
    if (held && (held.definition.tows || job.row.carts.includes(held.id))) return true;
    return job.row.carts.some((id) => cartById(id)?.towedBy);
  }

  let lastMinute = null;

  return {
    rows,

    /** Today's strike cards, if today's shift is one a lawn is struck on. Pure. */
    cardsFor(day) {
      const shift = shiftFor(day);
      return rows.filter((row) => row.shifts.includes(shift.id)).map((row) => cardFor(row, day, shift));
    },

    /** The row behind a card, or null for a card that is not a strike. */
    rowFor: (beo) => (beo?.teardown ? byId[beo.teardown] ?? null : null),

    get active() {
      return job;
    },

    /** Zones the job you are on puts you in. */
    works(zone) {
      return Boolean(job?.row.works.includes(zone));
    },

    /**
     * What the job sends you to do, which nobody makes a note of: the tug being
     * driven. A strike is a dozen passes of a loud cart past the same crew and
     * the same Valet Captain, and a pass is odd only when it is not the job —
     * so without this the Banquet Captain files on the third lap and the job
     * suspends whoever does it. Only the ride: a cart going over, a person
     * clipped, somewhere you should not be, are all still what they were.
     */
    excuses(event) {
      if (!job || event.actor !== 'player' || event.cause !== 'ride') return false;
      return Boolean(OBJECTS[event.kind]?.tows);
    },

    /**
     * Taking one: the lawn's carts are back on it, empty, and the crew are out
     * there. They were already out there; this is when you find them.
     */
    begin(beo) {
      const row = byId[beo.teardown];
      if (!row) return false;
      for (const id of row.carts) {
        const cart = cartById(id);
        if (cart && wheeled.active !== cart && !cart.towedBy) wheeled.home(cart);
      }
      job = {
        row,
        beoId: beo.id,
        left: Object.fromEntries(row.stacks.map((stack) => [stack.prop, stack.count])),
        crew: {},
        spilled: [],
      };
      lastMinute = clock.day * 1440 + clock.minutes;
      return true;
    },

    run(dt) {
      if (!job) return;
      // Escorted off the property, or anything else that took the card off you.
      if (activeBeo()?.id !== job.beoId) {
        for (const npc of strikers()) npc.unpost();
        job = null;
        lastMinute = null;
        return;
      }
      const now = clock.day * 1440 + clock.minutes;
      const minutes = lastMinute === null ? 0 : Math.max(0, now - lastMinute);
      lastMinute = now;
      for (const npc of strikers()) work(npc, dt, minutes);
    },

    counts,

    /** The grade for what is on the dock now. */
    grade({ minutesLate = 0 } = {}) {
      const c = counts();
      if (!c) return null;
      return gradeTeardown({ ...c, minutesLate });
    },

    /** Everybody back to their own posts. The carts stay wherever you left them. */
    finish() {
      for (const npc of strikers()) npc.unpost();
      job = null;
      lastMinute = null;
    },

    clear() {
      for (const npc of strikers()) npc.unpost();
      job = null;
      lastMinute = null;
    },

    /** What the HUD's objective block says while one is yours. */
    objective(active) {
      const c = counts();
      if (!c) return null;
      const remaining = active.times.setupBy - clock.minutes;
      const done = c.onDock === c.carts && !c.left;
      return {
        title: `${active.beo.code} · ${active.beo.title}`,
        // The lawn until it is struck, then the dock it is filed on.
        where: { room: done ? job.row.dock : job.row.lawn },
        line: done
          ? 'Everything is on the dock. File the strike with [R], on the dock'
          : remaining >= 0
            ? `Tow the carts to the apron, push them onto the dock · by ${formatTime(active.times.setupBy)} · ${formatSpan(remaining)} left`
            : `OVERDUE by ${formatSpan(-remaining)} · the lawn is not struck`,
        tone: done ? 'good' : remaining < 0 ? 'bad' : remaining < 20 ? 'warn' : 'good',
        progress: `${c.onDock} of ${count(c.carts, 'cart')} on the dock · ${c.delivered}/${count(c.total, 'piece')} · ${c.spilled} spilled`,
        submit: `[R]  file the strike, on the dock  ·  ${c.onDock} of ${count(c.carts, 'cart')} in`,
      };
    },

    toJSON: () =>
      job && {
        row: job.row.id,
        beoId: job.beoId,
        left: job.left,
        crew: Object.fromEntries(
          Object.entries(job.crew).map(([id, state]) => [id, { ...state, posted: false, busy: 0 }])
        ),
        spilled: job.spilled,
      },

    load(state) {
      for (const npc of strikers()) npc.unpost();
      job = null;
      lastMinute = null;
      if (!state || !byId[state.row]) return;
      job = {
        row: byId[state.row],
        beoId: state.beoId,
        left: { ...state.left },
        crew: Object.fromEntries(Object.entries(state.crew ?? {}).map(([id, entry]) => [id, { ...entry, posted: false }])),
        spilled: [...(state.spilled ?? [])],
      };
      lastMinute = clock.day * 1440 + clock.minutes;
    },
  };
}
